import { useState, useEffect } from 'react';

export default function HomePage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    acceptTerms: false
  });
  const [showToast, setShowToast] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showModal, setShowModal] = useState(false);

  // Modal timer effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowModal(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'El nombre es obligatorio';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'El apellido es obligatorio';
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'El email es obligatorio';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Formato de email inválido';
    }
    
    const phoneRegex = /^\+[1-9]\d{1,14}$/;
    if (!formData.phone.trim()) {
      newErrors.phone = 'El teléfono es obligatorio';
    } else if (!phoneRegex.test(formData.phone)) {
      newErrors.phone = 'Formato de teléfono inválido (ej: +1800...)';
    }
    
    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'Debe aceptar los términos y condiciones';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone
      };

      console.log('Payload JSON:', payload);

      const response = await fetch('https://services.leadconnectorhq.com/hooks/ut8oNafoY1iyDEfAFe0z/webhook-trigger/ac021827-2c14-4da6-b497-db8668b848a5', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setShowToast(true);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          acceptTerms: false
        });
        setShowModal(false); // Close modal on success
        setTimeout(() => setShowToast(false), 3000);
      }
    } catch (error) {
      console.error('Error al enviar formulario:', error);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const closeModal = () => {
    setShowModal(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Hero Section */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A] via-[#1A1A1A] to-[#0A0A0A]"></div>
        
        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          {/* Logo in Hero - Made much larger */}
          <div className="mb-8">
            <img 
              src="/Logo_Cashflow_VIP.svg" 
              alt="CashFlow VIP Logo" 
              className="h-40 w-auto mx-auto mb-4"
            />
          </div>

          {/* Gift Card Animation */}
          <div className="mb-12 px-4 sm:px-6">
            <a href="#gc-form" className="gift-card-container inline-block cursor-pointer">
              <div className="gift-card relative w-80 sm:w-96 h-52 sm:h-60 mx-auto bg-gradient-to-br from-[#D4AF37] via-[#F4E4A6] to-[#B8941F] rounded-3xl shadow-2xl transform perspective-1000 border-2 border-[#F4E4A6]">
                {/* Card Background Pattern */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A]/10 to-transparent rounded-3xl"></div>
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute top-4 left-4 w-32 h-32 border border-[#0A0A0A]/20 rounded-full"></div>
                  <div className="absolute bottom-4 right-4 w-24 h-24 border border-[#0A0A0A]/20 rounded-full"></div>
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-40 border border-[#0A0A0A]/10 rounded-full"></div>
                </div>
                
                {/* Card Header */}
                <div className="absolute top-6 left-6 right-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-[#0A0A0A] font-bold text-2xl tracking-wide drop-shadow-sm">
                        CashFlow VIP
                      </div>
                      <div className="text-[#0A0A0A]/80 text-sm font-medium mt-1">
                        GIFT CARD
                      </div>
                    </div>
                    <div className="w-12 h-8 bg-gradient-to-r from-[#0A0A0A] to-[#1A1A1A] rounded-md flex items-center justify-center shadow-lg">
                      <i className="ri-vip-crown-line text-[#D4AF37] text-lg"></i>
                    </div>
                  </div>
                </div>

                {/* Card Chip */}
                <div className="absolute top-20 left-6">
                  <div className="w-12 h-9 bg-gradient-to-br from-[#0A0A0A] to-[#2A2A2A] rounded-lg shadow-lg border border-[#D4AF37]/30">
                    <div className="w-full h-full bg-gradient-to-br from-[#D4AF37]/20 to-transparent rounded-lg"></div>
                  </div>
                </div>

                {/* Card Number */}
                <div className="absolute top-32 left-6 right-6 hidden sm:block">
                  <div className="text-[#0A0A0A] font-mono text-lg tracking-widest font-bold drop-shadow-sm">
                    •••• •••• •••• 0100
                  </div>
                </div>

                {/* Card Value */}
                <div className="absolute bottom-6 right-6">
                  <div className="text-right">
                    <div className="text-[#0A0A0A]/80 text-sm font-medium">
                      VALOR
                    </div>
                    <div className="text-[#0A0A0A] font-bold text-4xl drop-shadow-sm">
                      $100
                    </div>
                  </div>
                </div>

                {/* Card Holder */}
                <div className="absolute bottom-6 left-6">
                  <div className="text-[#0A0A0A]/80 text-sm font-medium">
                    VÁLIDA HASTA
                  </div>
                  <div className="text-[#0A0A0A] text-sm font-mono font-bold">
                    11/26
                  </div>
                </div>

                {/* Shine Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -skew-x-12 animate-shine rounded-3xl"></div>
                
                {/* Metallic Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#F4E4A6]/20 via-transparent to-[#B8941F]/20 rounded-3xl"></div>
                
                {/* Edge Highlight */}
                <div className="absolute inset-0 rounded-3xl border-2 border-gradient-to-r from-[#F4E4A6] via-[#D4AF37] to-[#B8941F] shadow-inner"></div>
              </div>
            </a>
          </div>

          <h2 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-[#D4AF37] to-[#F4E4A6] bg-clip-text text-transparent">
            CashFlow VIP Gift Card
          </h2>
          <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-2xl mx-auto">
            Aplícala en servicios elegibles y transforma tu vida
          </p>
          
          <a href="#gc-form" className="inline-flex items-center px-8 py-4 bg-[#D4AF37] text-[#0A0A0A] font-bold text-lg rounded-full hover:bg-[#F4E4A6] transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-[#D4AF37]/25">
            Registrar Gift Card
            <i className="ri-arrow-down-line ml-2 text-xl"></i>
          </a>
        </div>
      </section>

      {/* Gift Card Registration Form */}
      <section id="gc-form" className="py-20 bg-gradient-to-b from-[#0A0A0A] to-[#1A1A1A]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#D4AF37] mb-4">
              Registro de Gift Card
            </h2>
            <p className="text-xl text-gray-300">
              Completa tus datos para activar tu Gift Card de $100
            </p>
          </div>

          <form 
            onSubmit={handleSubmit} 
            className="bg-[#1A1A1A] p-8 rounded-2xl shadow-2xl border border-[#D4AF37]/20"
            data-readdy-form
            id="gift-card-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-[#D4AF37] mb-2">
                  Nombre(s) *
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-lg text-white placeholder-gray-400 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all duration-300"
                  placeholder="Tu nombre"
                  aria-describedby={errors.firstName ? 'firstName-error' : undefined}
                />
                {errors.firstName && (
                  <p id="firstName-error" className="mt-1 text-sm text-red-400" role="alert">
                    {errors.firstName}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-[#D4AF37] mb-2">
                  Apellido(s) *
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-lg text-white placeholder-gray-400 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all duration-300"
                  placeholder="Tu apellido"
                  aria-describedby={errors.lastName ? 'lastName-error' : undefined}
                />
                {errors.lastName && (
                  <p id="lastName-error" className="mt-1 text-sm text-red-400" role="alert">
                    {errors.lastName}
                  </p>
                )}
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="email" className="block text-sm font-medium text-[#D4AF37] mb-2">
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-lg text-white placeholder-gray-400 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all duration-300"
                placeholder="tu@email.com"
                aria-describedby={errors.email ? 'email-error' : undefined}
              />
              {errors.email && (
                <p id="email-error" className="mt-1 text-sm text-red-400" role="alert">
                  {errors.email}
                </p>
              )}
            </div>

            <div className="mb-6">
              <label htmlFor="phone" className="block text-sm font-medium text-[#D4AF37] mb-2">
                Teléfono *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-lg text-white placeholder-gray-400 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all duration-300"
                placeholder="+1800..."
                aria-describedby={errors.phone ? 'phone-error' : undefined}
              />
              {errors.phone && (
                <p id="phone-error" className="mt-1 text-sm text-red-400" role="alert">
                  {errors.phone}
                </p>
              )}
            </div>

            <div className="mb-8">
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={(e) => handleInputChange('acceptTerms', e.target.checked)}
                  className="mt-1 w-5 h-5 text-[#D4AF37] bg-[#0A0A0A] border-[#D4AF37]/30 rounded focus:ring-[#D4AF37] focus:ring-2"
                  aria-describedby={errors.acceptTerms ? 'terms-error' : undefined}
                />
                <span className="text-sm text-gray-300">
                  Acepto los <a href="#terms" className="text-[#D4AF37] hover:underline">Términos y Condiciones</a> *
                </span>
              </label>
              {errors.acceptTerms && (
                <p id="terms-error" className="mt-1 text-sm text-red-400" role="alert">
                  {errors.acceptTerms}
                </p>
              )}
            </div>

            <button
              type="submit"
              className="w-full bg-[#D4AF37] text-[#0A0A0A] font-bold py-4 px-6 rounded-lg hover:bg-[#F4E4A6] transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-[#D4AF37]/25 whitespace-nowrap"
            >
              Registrar Gift Card
            </button>
          </form>
        </div>
      </section>

      {/* About CashFlow VIP */}
      <section id="about" className="py-20 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <img 
              src="/Logo_Cashflow_VIP.svg" 
              alt="CashFlow VIP Logo" 
              className="h-32 w-auto mx-auto mb-6"
            />
            <h2 className="text-4xl font-bold text-[#D4AF37] mb-4">
              Sobre CashFlow VIP
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto">
              Somos una firma enfocada en reparación de crédito, preparación para funding y automatización de procesos (CRM, pipelines, integraciones). Operamos con mentalidad de datos + ejecución para que veas avances reales en menos tiempo.
            </p>
          </div>

          {/* Lo que hacemos */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-[#D4AF37] text-center mb-12">Lo que hacemos</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="text-center p-6 bg-[#1A1A1A] rounded-xl border border-[#D4AF37]/20 hover:border-[#D4AF37]/40 transition-all duration-300">
                <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-file-search-line text-2xl text-[#0A0A0A]"></i>
                </div>
                <h4 className="text-lg font-semibold text-[#D4AF37] mb-3">Diagnóstico de Crédito</h4>
                <p className="text-gray-300 text-sm">Análisis completo de tu perfil crediticio y plan de acción priorizado para mejores resultados.</p>
              </div>

              <div className="text-center p-6 bg-[#1A1A1A] rounded-xl border border-[#D4AF37]/20 hover:border-[#D4AF37]/40 transition-all duration-300">
                <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-2xl text-[#0A0A0A]"></i>
                </div>
                <h4 className="text-lg font-semibold text-[#D4AF37] mb-3">Disputas y Gestión</h4>
                <p className="text-gray-300 text-sm">Gestión documental con metodología clara para disputas efectivas y resultados medibles.</p>
              </div>

              <div className="text-center p-6 bg-[#1A1A1A] rounded-xl border border-[#D4AF37]/20 hover:border-[#D4AF37]/40 transition-all duration-300">
                <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-bank-line text-2xl text-[#0A0A0A]"></i>
                </div>
                <h4 className="text-lg font-semibold text-[#D4AF37] mb-3">Estrategia de Funding</h4>
                <p className="text-gray-300 text-sm">Preparación de perfil, secuencia de aplicaciones estratégicas y optimización de límites.</p>
              </div>

              <div className="text-center p-6 bg-[#1A1A1A] rounded-xl border border-[#D4AF37]/20 hover:border-[#D4AF37]/40 transition-all duration-300">
                <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-settings-3-line text-2xl text-[#0A0A0A]"></i>
                </div>
                <h4 className="text-lg font-semibold text-[#D4AF37] mb-3">Automatización CRM</h4>
                <p className="text-gray-300 text-sm">Embudos, recordatorios, pagos (Square/Stripe) y reportes automatizados para mayor eficiencia.</p>
              </div>

              <div className="text-center p-6 bg-[#1A1A1A] rounded-xl border border-[#D4AF37]/20 hover:border-[#D4AF37]/40 transition-all duration-300">
                <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-graduation-cap-line text-2xl text-[#0A0A0A]"></i>
                </div>
                <h4 className="text-lg font-semibold text-[#D4AF37] mb-3">Educación Financiera</h4>
                <p className="text-gray-300 text-sm">Educación financiera aplicada para tomar decisiones consistentes y sostenibles.</p>
              </div>
            </div>
          </div>

          {/* Por qué elegirnos */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-[#D4AF37] text-center mb-12">Por qué elegirnos</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="p-6 bg-[#1A1A1A] rounded-xl border border-[#D4AF37]/20">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-route-line text-xl text-[#0A0A0A]"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-[#D4AF37] mb-2">Procesos Claros</h4>
                    <p className="text-gray-300 text-sm">Mapa de etapas, tiempos estimados y entregables visibles en cada paso del proceso.</p>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-[#1A1A1A] rounded-xl border border-[#D4AF37]/20">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-target-line text-xl text-[#0A0A0A]"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-[#D4AF37] mb-2">Enfoque en Resultados</h4>
                    <p className="text-gray-300 text-sm">Objetivos medibles por etapa: perfil crediticio, límites y tasas optimizadas.</p>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-[#1A1A1A] rounded-xl border border-[#D4AF37]/20">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-user-heart-line text-xl text-[#0A0A0A]"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-[#D4AF37] mb-2">Atención Personalizada</h4>
                    <p className="text-gray-300 text-sm">Consultoría uno a uno y acompañamiento personalizado en cada hito importante.</p>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-[#1A1A1A] rounded-xl border border-[#D4AF37]/20">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-shield-keyhole-line text-xl text-[#0A0A0A]"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-[#D4AF37] mb-2">Datos y Seguridad</h4>
                    <p className="text-gray-300 text-sm">Manejo responsable de información, acceso restringido y buenas prácticas de privacidad.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cómo trabajamos */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-[#D4AF37] text-center mb-12">Cómo trabajamos</h3>
            <div className="relative">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4 relative">
                    <span className="text-2xl font-bold text-[#0A0A0A]">1</span>
                  </div>
                  <h4 className="text-lg font-semibold text-[#D4AF37] mb-2">Diagnóstico</h4>
                  <p className="text-gray-300 text-sm">Revisión completa de perfil y definición de metas específicas.</p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-[#0A0A0A]">2</span>
                  </div>
                  <h4 className="text-lg font-semibold text-[#D4AF37] mb-2">Plan</h4>
                  <p className="text-gray-300 text-sm">Rutas de mejora personalizadas y cronograma detallado.</p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-[#0A0A0A]">3</span>
                  </div>
                  <h4 className="text-lg font-semibold text-[#D4AF37] mb-2">Ejecución</h4>
                  <p className="text-gray-300 text-sm">Disputas, optimización y documentación estratégica.</p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-[#0A0A0A]">4</span>
                  </div>
                  <h4 className="text-lg font-semibold text-[#D4AF37] mb-2">Funding</h4>
                  <p className="text-gray-300 text-sm">Preparación y aplicaciones estratégicas para mejores términos.</p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-[#0A0A0A]">5</span>
                  </div>
                  <h4 className="text-lg font-semibold text-[#D4AF37] mb-2">Seguimiento</h4>
                  <p className="text-gray-300 text-sm">Métricas de progreso y planificación de próximos pasos.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Trust Section */}
          <div className="bg-[#1A1A1A] rounded-2xl p-8 border border-[#D4AF37]/20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold text-[#D4AF37] mb-4">Confianza y Seguridad</h3>
                <div className="flex items-center space-x-6 mb-6">
                  <div className="flex items-center space-x-2">
                    <i className="ri-shield-check-line text-[#D4AF37] text-xl"></i>
                    <span className="text-sm text-gray-300">Datos Seguros</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <i className="ri-lock-line text-[#D4AF37] text-xl"></i>
                    <span className="text-sm text-gray-300">Privacidad Garantizada</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <i className="ri-verified-badge-line text-[#D4AF37] text-xl"></i>
                    <span className="text-sm text-gray-300">Buenas Prácticas</span>
                  </div>
                </div>
                <p className="text-sm text-gray-400 mb-6">
                  No ofrecemos asesoría legal; trabajamos con políticas y procedimientos internos alineados a buenas prácticas del sector.
                </p>
                <a
                  href="https://web.cashflow.vip/widget/booking/dZafhFlPIO82YbvGywB0"
                  target="_blank"
                  className="bg-[#D4AF37] text-[#0A0A0A] font-bold py-3 px-6 rounded-lg hover:bg-[#F4E4A6] transition-all duration-300 transform hover:scale-105 whitespace-nowrap"
                >
                  Agenda tu diagnóstico
                </a>
              </div>
              
              <div className="space-y-4">
                <div className="bg-[#0A0A0A] p-4 rounded-lg border border-[#D4AF37]/20">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="flex text-[#D4AF37]">
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                    </div>
                    <span className="text-sm font-semibold text-white">M.G.</span>
                  </div>
                  <p className="text-sm text-gray-300">"Pasamos del desorden a un plan claro. Vimos avances desde el primer mes."</p>
                </div>
                
                <div className="bg-[#0A0A0A] p-4 rounded-lg border border-[#D4AF37]/20">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="flex text-[#D4AF37]">
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                    </div>
                    <span className="text-sm font-semibold text-white">C.R.</span>
                  </div>
                  <p className="text-sm text-gray-300">"Profesionales y metódicos. Funding con mejor tasa de lo esperado."</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Terms and Conditions */}
      <section id="terms" className="py-20 bg-[#1A1A1A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#D4AF37] mb-4">
              Términos y Condiciones de la Gift Card
            </h2>
          </div>

          <div className="bg-[#0A0A0A] p-8 rounded-2xl border border-[#D4AF37]/20">
            <div className="prose prose-invert max-w-none">
              <h3 className="text-xl font-semibold text-[#D4AF37] mb-4">Condiciones de Uso</h3>
              
              <ul className="space-y-3 text-gray-300 mb-8">
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-[#D4AF37] mt-1"></i>
                  <span><strong>No transferible:</strong> Esta Gift Card es personal e intransferible. Solo puede ser utilizada por el titular registrado.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-[#D4AF37] mt-1"></i>
                  <span><strong>No canjeable por efectivo:</strong> El valor de la Gift Card no puede ser convertido a dinero en efectivo bajo ninguna circunstancia.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-[#D4AF37] mt-1"></i>
                  <span><strong>No acumulable:</strong> No se puede combinar con otras promociones, descuentos o Gift Cards.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-[#D4AF37] mt-1"></i>
                  <span><strong>Una por cliente:</strong> Límite de una Gift Card por persona. Se requiere verificación de identidad.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-[#D4AF37] mt-1"></i>
                  <span><strong>Vigencia:</strong> Válida hasta el 14 de noviembre de 2026. No se extiende la fecha de vencimiento.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-[#D4AF37] mt-1"></i>
                  <span><strong>Servicios elegibles:</strong> Aplicable únicamente a servicios de consultoría financiera y optimización de flujo de efectivo.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-[#D4AF37] mt-1"></i>
                  <span><strong>Verificación requerida:</strong> Sujeta a verificación de identidad y validación de datos antes de su activación.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] border-t border-[#D4AF37]/20 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="mb-4">
              <img 
                src="/Logo_Cashflow_VIP.svg" 
                alt="CashFlow VIP Logo" 
                className="h-24 w-auto mx-auto mb-2"
              />
            </div>
            <p className="text-gray-400 text-sm mb-4">
              © 2025 CashFlow VIP. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>

      {/* Toast Notification */}
      {showToast && (
        <div className="fixed top-4 right-4 z-50 bg-[#D4AF37] text-[#0A0A0A] px-6 py-3 rounded-lg shadow-lg transform transition-all duration-300 animate-bounce">
          <div className="flex items-center space-x-2">
            <i className="ri-check-line text-xl"></i>
            <span className="font-semibold">Registro enviado exitosamente</span>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={closeModal}
          ></div>
          
          {/* Modal Content */}
          <div className="relative bg-[#1A1A1A] rounded-2xl shadow-2xl border border-[#D4AF37]/20 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Close Button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-[#D4AF37] transition-colors duration-300 z-10"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            {/* Modal Header */}
            <div className="text-center p-8 pb-4">
              <div className="mb-4">
                <img 
                  src="/Logo_Cashflow_VIP.svg" 
                  alt="CashFlow VIP Logo" 
                  className="h-20 w-auto mx-auto mb-4"
                />
              </div>
              <h2 className="text-3xl font-bold text-[#D4AF37] mb-2">
                ¡No te pierdas tu Gift Card!
              </h2>
              <p className="text-lg text-gray-300">
                Registra tus datos ahora y obtén $100 para servicios de CashFlow VIP
              </p>
            </div>

            {/* Modal Form */}
            <div className="p-8 pt-4">
              <form 
                onSubmit={handleSubmit} 
                className="space-y-6"
                data-readdy-form
                id="modal-gift-card-form"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="modal-firstName" className="block text-sm font-medium text-[#D4AF37] mb-2">
                      Nombre(s) *
                    </label>
                    <input
                      type="text"
                      id="modal-firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-lg text-white placeholder-gray-400 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all duration-300"
                      placeholder="Tu nombre"
                      aria-describedby={errors.firstName ? 'modal-firstName-error' : undefined}
                    />
                    {errors.firstName && (
                      <p id="modal-firstName-error" className="mt-1 text-sm text-red-400" role="alert">
                        {errors.firstName}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="modal-lastName" className="block text-sm font-medium text-[#D4AF37] mb-2">
                      Apellido(s) *
                    </label>
                    <input
                      type="text"
                      id="modal-lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-lg text-white placeholder-gray-400 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all duration-300"
                      placeholder="Tu apellido"
                      aria-describedby={errors.lastName ? 'modal-lastName-error' : undefined}
                    />
                    {errors.lastName && (
                      <p id="modal-lastName-error" className="mt-1 text-sm text-red-400" role="alert">
                        {errors.lastName}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="modal-email" className="block text-sm font-medium text-[#D4AF37] mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="modal-email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-lg text-white placeholder-gray-400 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all duration-300"
                    placeholder="tu@email.com"
                    aria-describedby={errors.email ? 'modal-email-error' : undefined}
                  />
                  {errors.email && (
                    <p id="modal-email-error" className="mt-1 text-sm text-red-400" role="alert">
                      {errors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="modal-phone" className="block text-sm font-medium text-[#D4AF37] mb-2">
                    Teléfono *
                  </label>
                  <input
                    type="tel"
                    id="modal-phone"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-lg text-white placeholder-gray-400 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all duration-300"
                    placeholder="+1800..."
                    aria-describedby={errors.phone ? 'modal-phone-error' : undefined}
                  />
                  {errors.phone && (
                    <p id="modal-phone-error" className="mt-1 text-sm text-red-400" role="alert">
                      {errors.phone}
                    </p>
                  )}
                </div>

                <div>
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="acceptTerms"
                      checked={formData.acceptTerms}
                      onChange={(e) => handleInputChange('acceptTerms', e.target.checked)}
                      className="mt-1 w-5 h-5 text-[#D4AF37] bg-[#0A0A0A] border-[#D4AF37]/30 rounded focus:ring-[#D4AF37] focus:ring-2"
                      aria-describedby={errors.acceptTerms ? 'modal-terms-error' : undefined}
                    />
                    <span className="text-sm text-gray-300">
                      Acepto los <a href="#terms" className="text-[#D4AF37] hover:underline" onClick={closeModal}>Términos y Condiciones</a> *
                    </span>
                  </label>
                  {errors.acceptTerms && (
                    <p id="modal-terms-error" className="mt-1 text-sm text-red-400" role="alert">
                      {errors.acceptTerms}
                    </p>
                  )}
                </div>

                <div className="flex space-x-4">
                  <button
                    type="submit"
                    className="w-full bg-[#D4AF37] text-[#0A0A0A] font-bold py-4 px-6 rounded-lg hover:bg-[#F4E4A6] transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-[#D4AF37]/25 whitespace-nowrap"
                  >
                    Registrar Gift Card
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Custom Styles */}
      <style jsx>{`
        .gift-card {
          animation: cardFloat 4s ease-in-out infinite, cardRotate 8s ease-in-out infinite;
          transform-style: preserve-3d;
        }
        
        @keyframes cardFloat {
          0%, 100% { 
            transform: translateY(0px) rotateX(0deg) rotateY(0deg); 
            box-shadow: 0 25px 50px -12px rgba(212, 175, 55, 0.4);
          }
          25% { 
            transform: translateY(-8px) rotateX(2deg) rotateY(-2deg); 
            box-shadow: 0 35px 60px -12px rgba(212, 175, 55, 0.5);
          }
          50% { 
            transform: translateY(-12px) rotateX(0deg) rotateY(3deg); 
            box-shadow: 0 40px 70px -12px rgba(212, 175, 55, 0.6);
          }
          75% { 
            transform: translateY(-8px) rotateX(-2deg) rotateY(-1deg); 
            box-shadow: 0 35px 60px -12px rgba(212, 175, 55, 0.5);
          }
        }
        
        @keyframes cardRotate {
          0%, 90%, 100% { transform: rotateY(0deg); }
          45%, 55% { transform: rotateY(15deg); }
        }
        
        @keyframes shine {
          0% { transform: translateX(-120%) skewX(-15deg); opacity: 0; }
          50% { opacity: 1; }
          100% { transform: translateX(120%) skewX(-15deg); opacity: 0; }
        }
        
        .animate-shine {
          animation: shine 3s ease-in-out infinite;
        }
        
        .perspective-1000 {
          perspective: 1000px;
        }
        
        .gift-card-container:hover .gift-card {
          animation-play-state: paused;
          transform: translateY(-15px) rotateX(5deg) rotateY(-5deg) scale(1.05);
          box-shadow: 0 50px 80px -12px rgba(212, 175, 55, 0.7);
          transition: all 0.3s ease-out;
        }
        
        /* Smooth scrolling */
        html {
          scroll-behavior: smooth;
        }
        
        /* Focus styles for accessibility */
        input:focus, button:focus, a:focus {
          outline: 2px solid #D4AF37;
          outline-offset: 2px;
        }
      `}</style>
    </div>
  );
}
